'use client'

import * as React from 'react'
import useSWR from 'swr'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import type { CodingLog, CodingLogStep } from '@/lib/types'
import type { CodingLogAnalysis } from '@/app/api/dashboard/coding-logs/analyze/route'
import { fetcher } from '@/lib/utils'
import { cn } from '@/lib/utils'
import {
  BugIcon,
  Brain,
  Loader2,
  Clock,
  RefreshCw,
  AlertCircle,
  TestTube,
} from 'lucide-react'

interface CodingLogDetailModalProps {
  projectId: string
  projectName: string
  firstCodingLogId?: string | null
  onClose?: () => void
}

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7300',
  '#00ff00',
  '#0088fe',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884d8',
]

export default function CodingLogDetailModal({
  projectId,
  projectName,
  firstCodingLogId,
  onClose,
}: CodingLogDetailModalProps) {
  const [open, setOpen] = React.useState(true)
  const [analyzing, setAnalyzing] = React.useState(false)
  const [analysisResult, setAnalysisResult] =
    React.useState<CodingLogAnalysis | null>(null)

  // Fetch all coding logs for this project
  const { data: logsData, isLoading } = useSWR<{
    data: CodingLog[]
    total: number
  }>(`/api/dashboard/coding-logs?projectId=${projectId}`, fetcher)

  const formatTime = React.useCallback((timestamp?: number) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp * 1000).toLocaleString()
  }, [])

  const formatDuration = React.useCallback((seconds: number): string => {
    if (!seconds || seconds < 0) return '0s'
    if (seconds < 60) return `${seconds.toFixed(1)}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (minutes < 60) {
      return remainingSeconds > 0
        ? `${minutes}m ${remainingSeconds.toFixed(0)}s`
        : `${minutes}m`
    }
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }, [])

  // Calculate project statistics
  const projectStats = React.useMemo(() => {
    if (!logsData?.data) return null

    const logs = logsData.data
    let totalCodingTime = 0
    const logDetails: Array<{
      msg_id: string
      time: number
      time_formatted: string
      duration: number
      duration_formatted: string
      status?: string
    }> = []

    let longestStep: {
      type?: string
      run_time?: number
      error?: string
      reason: string
    } | null = null
    let maxRunTime = 0

    for (const log of logs) {
      let logDuration = 0
      const logAny = log as any
      if (
        logAny.total_run_seconds !== undefined &&
        logAny.total_run_seconds !== null
      ) {
        logDuration = logAny.total_run_seconds
      } else if (
        logAny.total_run_minutes !== undefined &&
        logAny.total_run_minutes !== null
      ) {
        logDuration = logAny.total_run_minutes * 60
      } else if (
        log.steps &&
        Array.isArray(log.steps) &&
        log.steps.length > 0
      ) {
        const stepTimes = log.steps
          .map((step: any) => step.run_time || 0)
          .filter((time: number) => time > 0)
        if (stepTimes.length > 0) {
          logDuration = stepTimes.reduce(
            (sum: number, time: number) => sum + time,
            0,
          )
        }
      }

      totalCodingTime += logDuration

      logDetails.push({
        msg_id: log.msg_id || '',
        time: log.time || 0,
        time_formatted: formatTime(log.time),
        duration: logDuration,
        duration_formatted: formatDuration(logDuration),
        status: log.status,
      })

      // Find longest step
      if (log.steps && Array.isArray(log.steps)) {
        for (const step of log.steps) {
          const runTime = step.run_time || 0
          if (runTime > maxRunTime) {
            maxRunTime = runTime
            const reasonParts: string[] = []
            if (step.type) {
              reasonParts.push(`Step type: ${step.type}`)
            }
            if (step.error) {
              reasonParts.push(`Error: ${step.error}`)
            }
            if (step.complexity) {
              reasonParts.push(`Complexity: ${step.complexity}`)
            }
            longestStep = {
              type: step.type,
              run_time: runTime,
              error: step.error,
              reason:
                reasonParts.length > 0
                  ? reasonParts.join(' | ')
                  : `Longest step took ${formatDuration(runTime)}`,
            }
          }
        }
      }
    }

    return {
      total_coding_time: totalCodingTime,
      total_coding_time_formatted: formatDuration(totalCodingTime),
      retry_count: logs.length,
      logs: logDetails.sort((a, b) => b.time - a.time),
      longest_step: longestStep,
    }
  }, [logsData, formatDuration, formatTime])

  const handleAnalyze = async () => {
    setAnalyzing(true)
    setAnalysisResult(null)

    try {
      const response = await fetch('/api/dashboard/coding-logs/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      })

      const data = await response.json()

      if (data.success && data.analysis) {
        setAnalysisResult(data.analysis)
      } else {
        console.error('Analysis failed:', data.error)
      }
    } catch (error) {
      console.error('Error analyzing coding logs:', error)
    } finally {
      setAnalyzing(false)
    }
  }

  // Find the first coding log
  const firstLog = React.useMemo(() => {
    if (!logsData?.data) return null
    // If firstCodingLogId is provided, use it; otherwise find the earliest log
    if (firstCodingLogId) {
      return logsData.data.find(
        (log) => log._id.toString() === firstCodingLogId,
      )
    }
    // Sort by time and find the first one
    const sorted = [...logsData.data].sort((a, b) => {
      const timeA = a.time || 0
      const timeB = b.time || 0
      return timeA - timeB
    })
    return sorted[0] || null
  }, [logsData, firstCodingLogId])

  const handleClose = () => {
    setOpen(false)
    onClose?.()
  }

  // Prepare pie chart data from steps
  const pieChartData = React.useMemo(() => {
    if (!firstLog?.steps || firstLog.steps.length === 0) return []

    // Group steps by type and calculate total time
    const stepTypeMap = new Map<string, { time: number; count: number }>()

    firstLog.steps.forEach((step: CodingLogStep) => {
      const type = step.type || 'unknown'
      const runTime = step.run_time || 0
      const existing = stepTypeMap.get(type) || { time: 0, count: 0 }
      stepTypeMap.set(type, {
        time: existing.time + runTime,
        count: existing.count + 1,
      })
    })

    return Array.from(stepTypeMap.entries()).map(([type, data]) => ({
      name: type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      value: data.time,
      count: data.count,
    }))
  }, [firstLog])

  // Find bug fix steps
  const bugFixSteps = React.useMemo(() => {
    if (!firstLog?.steps) return []
    return firstLog.steps.filter(
      (step: CodingLogStep) =>
        step.error || step.type?.includes('bug') || step.type?.includes('fix'),
    )
  }, [firstLog])

  const totalDuration = React.useMemo(() => {
    if (!firstLog?.steps || firstLog.steps.length === 0) return 0
    const steps = firstLog.steps
    const firstStepTime = steps[0]?.time || firstLog.time
    const lastStepTime =
      steps[steps.length - 1]?.time ||
      steps.reduce((max: number, step: CodingLogStep) => {
        return Math.max(max, step.time || 0)
      }, 0)
    if (firstStepTime && lastStepTime && lastStepTime > firstStepTime) {
      return lastStepTime - firstStepTime
    }
    return 0
  }, [firstLog])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] h-[85vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle>First Coding Details - {projectName}</DialogTitle>
          <DialogDescription>
            Project ID: {projectId}
            {firstLog && (
              <>
                {' | '}Message ID: {firstLog.msg_id}
                {' | '}Total Duration: {formatDuration(totalDuration)}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        ) : !firstLog ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-muted-foreground">No coding log found</div>
          </div>
        ) : (
          <Tabs
            defaultValue="overview"
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="grid w-full grid-cols-5 mx-6 mt-4 flex-shrink-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="stats">Statistics</TabsTrigger>
              <TabsTrigger value="steps">Steps</TabsTrigger>
              <TabsTrigger value="bugs">
                Bug Fixes
                {bugFixSteps.length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {bugFixSteps.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="analysis">AI Analysis</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 px-6 py-4">
              <TabsContent value="overview" className="mt-4 space-y-6">
                {/* Pie Chart */}
                {pieChartData.length > 0 && (
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Step Duration Distribution
                    </h3>
                    <div className="h-[400px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) =>
                              `${name}: ${(percent * 100).toFixed(0)}%`
                            }
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => formatDuration(value)}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">
                      Total Steps
                    </div>
                    <div className="text-2xl font-bold">
                      {firstLog.steps?.length || 0}
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">
                      Total Duration
                    </div>
                    <div className="text-2xl font-bold">
                      {formatDuration(totalDuration)}
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">
                      Bug Fixes
                    </div>
                    <div className="text-2xl font-bold text-red-500">
                      {bugFixSteps.length}
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground">Status</div>
                    <div className="text-2xl font-bold">
                      <Badge
                        variant={
                          firstLog.status === 'completed'
                            ? 'success'
                            : firstLog.status === 'failed'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {firstLog.status || 'unknown'}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Step Types Summary */}
                {pieChartData.length > 0 && (
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Step Types Summary
                    </h3>
                    <div className="space-y-2">
                      {pieChartData.map((item, index) => (
                        <div
                          key={item.name}
                          className="flex items-center justify-between p-3 bg-background rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded"
                              style={{
                                backgroundColor: COLORS[index % COLORS.length],
                              }}
                            />
                            <span className="font-medium">{item.name}</span>
                            <Badge variant="outline">{item.count} steps</Badge>
                          </div>
                          <span className="text-muted-foreground">
                            {formatDuration(item.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="stats" className="mt-4 space-y-4">
                {projectStats ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Total Coding Time
                        </div>
                        <div className="text-2xl font-bold mt-2">
                          {projectStats.total_coding_time_formatted}
                        </div>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <RefreshCw className="h-4 w-4" />
                          Retry Count
                        </div>
                        <div className="text-2xl font-bold mt-2">
                          {projectStats.retry_count}
                        </div>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          Longest Step
                        </div>
                        <div className="text-lg font-bold mt-2">
                          {projectStats.longest_step ? (
                            <div className="text-xs text-muted-foreground truncate">
                              {projectStats.longest_step.reason}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">N/A</span>
                          )}
                        </div>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="text-sm text-muted-foreground">
                          Total Logs
                        </div>
                        <div className="text-2xl font-bold mt-2">
                          {projectStats.logs.length}
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">
                        All Coding Logs
                      </h3>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {projectStats.logs.map((log, index) => (
                          <div
                            key={log.msg_id}
                            className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 rounded border bg-background text-sm"
                          >
                            <div className="flex flex-wrap items-center gap-2 md:gap-4 flex-1 min-w-0">
                              <span className="text-muted-foreground text-xs">
                                #{index + 1}
                              </span>
                              <span className="font-mono text-xs text-muted-foreground break-all">
                                {log.msg_id}
                              </span>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {log.time_formatted}
                              </span>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs">
                                  {log.duration_formatted}
                                </span>
                              </div>
                              {log.status && (
                                <Badge
                                  variant={
                                    log.status === 'completed'
                                      ? 'default'
                                      : log.status === 'failed'
                                        ? 'destructive'
                                        : 'secondary'
                                  }
                                  className="text-xs"
                                >
                                  {log.status}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No statistics available
                  </div>
                )}
              </TabsContent>

              <TabsContent value="steps" className="mt-4">
                <div className="space-y-4">
                  {firstLog.steps && firstLog.steps.length > 0 ? (
                    firstLog.steps.map((step: CodingLogStep, index) => (
                      <div
                        key={`${step.type}-${index}-${step.time || index}`}
                        className="border rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{index + 1}</Badge>
                            <span className="font-medium capitalize">
                              {step.type?.replace(/_/g, ' ') || 'Unknown Step'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {step.run_time !== undefined && (
                              <span>{formatDuration(step.run_time)}</span>
                            )}
                            {step.time && <span>{formatTime(step.time)}</span>}
                            {step.error && (
                              <Badge variant="destructive">Error</Badge>
                            )}
                          </div>
                        </div>

                        {step.error && (
                          <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                            <div className="flex items-center gap-2 text-red-400 mb-1">
                              <BugIcon className="w-4 h-4" />
                              <span className="font-semibold">Error:</span>
                            </div>
                            <div className="text-sm text-red-300 whitespace-pre-wrap">
                              {step.error}
                            </div>
                          </div>
                        )}

                        {step.path && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Path:</span>{' '}
                            <code className="bg-muted px-2 py-1 rounded">
                              {step.path}
                            </code>
                          </div>
                        )}

                        {step.description && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">
                              Description:
                            </span>{' '}
                            {step.description}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      No steps found
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="bugs" className="mt-4">
                <div className="space-y-4">
                  {bugFixSteps.length > 0 ? (
                    bugFixSteps.map((step: CodingLogStep, index) => (
                      <div
                        key={`bug-${index}-${step.time || index}`}
                        className="border border-red-500/20 rounded-lg p-4 space-y-3 bg-red-500/5"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <BugIcon className="w-5 h-5 text-red-500" />
                            <Badge variant="destructive">
                              Bug Fix #{index + 1}
                            </Badge>
                            <span className="font-medium capitalize">
                              {step.type?.replace(/_/g, ' ') || 'Unknown Step'}
                            </span>
                          </div>
                          {step.time && (
                            <span className="text-xs text-muted-foreground">
                              {formatTime(step.time)}
                            </span>
                          )}
                        </div>

                        {step.error && (
                          <div className="bg-red-500/20 border border-red-500/30 rounded p-3">
                            <div className="font-semibold text-red-400 mb-2">
                              Bug Reason:
                            </div>
                            <div className="text-sm text-red-200 whitespace-pre-wrap">
                              {step.error}
                            </div>
                          </div>
                        )}

                        {step.path && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">File:</span>{' '}
                            <code className="bg-muted px-2 py-1 rounded">
                              {step.path}
                            </code>
                          </div>
                        )}

                        {step.run_time !== undefined && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">
                              Fix Duration:
                            </span>{' '}
                            {formatDuration(step.run_time)}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      No bug fixes found
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="analysis" className="mt-4 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">AI Analysis</h3>
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    variant="outline"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="h-4 w-4 mr-2" />
                        Analyze with AI
                      </>
                    )}
                  </Button>
                </div>

                {analyzing && !analysisResult && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Analyzing coding logs with AI...
                      </p>
                    </div>
                  </div>
                )}

                {analysisResult && (
                  <Tabs defaultValue="summary" className="w-full">
                    <TabsList className="grid w-full grid-cols-5 mb-4">
                      <TabsTrigger value="summary">Summary</TabsTrigger>
                      <TabsTrigger value="failed-steps">
                        Failed Steps
                      </TabsTrigger>
                      <TabsTrigger value="prompts">Prompts</TabsTrigger>
                      <TabsTrigger value="workflow">Workflow</TabsTrigger>
                      <TabsTrigger value="testing">Testing</TabsTrigger>
                    </TabsList>

                    <TabsContent value="summary" className="space-y-4">
                      <div className="rounded-lg border p-4">
                        <h3 className="font-semibold mb-2">Overall Summary</h3>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {analysisResult.summary}
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent value="failed-steps" className="space-y-3">
                      <h3 className="font-semibold">Failed Steps Analysis</h3>
                      {analysisResult.failed_steps.length > 0 ? (
                        analysisResult.failed_steps.map((step, index) => (
                          <div
                            key={index}
                            className="rounded-lg border p-4 space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant="destructive">
                                  {step.step_type}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  Step #{step.step_index + 1}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {formatDuration(step.run_time)}
                              </span>
                            </div>
                            {step.error_message && (
                              <div className="rounded bg-red-50 dark:bg-red-950 p-2">
                                <p className="text-xs font-mono text-red-800 dark:text-red-200">
                                  {step.error_message}
                                </p>
                              </div>
                            )}
                            <p className="text-sm">{step.likely_cause}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              Log: {step.log_msg_id}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No failed steps identified.
                        </p>
                      )}
                    </TabsContent>

                    <TabsContent value="prompts" className="space-y-3">
                      <h3 className="font-semibold">Prompt Analysis</h3>
                      {analysisResult.prompt_analysis.suspected_prompts.length >
                      0 ? (
                        <div className="space-y-3">
                          {analysisResult.prompt_analysis.suspected_prompts.map(
                            (prompt, index) => (
                              <div
                                key={index}
                                className="rounded-lg border p-4 space-y-2"
                              >
                                <Badge variant="outline">
                                  {prompt.prompt_name}
                                </Badge>
                                <p className="text-sm font-medium">
                                  {prompt.reason}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Evidence: {prompt.evidence}
                                </p>
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No prompt issues identified.
                        </p>
                      )}
                      {analysisResult.prompt_analysis.prompt_issues.length >
                        0 && (
                        <div className="rounded-lg border p-4 space-y-2">
                          <h4 className="font-medium text-sm">
                            Potential Issues:
                          </h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                            {analysisResult.prompt_analysis.prompt_issues.map(
                              (issue, index) => (
                                <li key={index}>{issue}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="workflow" className="space-y-3">
                      <h3 className="font-semibold">Workflow Analysis</h3>
                      {analysisResult.workflow_analysis.suspected_workflow_steps
                        .length > 0 ? (
                        <div className="space-y-3">
                          {analysisResult.workflow_analysis.suspected_workflow_steps.map(
                            (step, index) => (
                              <div
                                key={index}
                                className="rounded-lg border p-4 space-y-2"
                              >
                                <div className="flex items-center justify-between">
                                  <Badge variant="outline">
                                    {step.workflow_step}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    Failed {step.frequency} time(s)
                                  </span>
                                </div>
                                <p className="text-sm">{step.reason}</p>
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No workflow issues identified.
                        </p>
                      )}
                      {analysisResult.workflow_analysis.workflow_issues.length >
                        0 && (
                        <div className="rounded-lg border p-4 space-y-2">
                          <h4 className="font-medium text-sm">
                            Workflow Problems:
                          </h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                            {analysisResult.workflow_analysis.workflow_issues.map(
                              (issue, index) => (
                                <li key={index}>{issue}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="testing" className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <TestTube className="h-4 w-4" />
                        Testing Guidance
                      </h3>
                      <div className="rounded-lg border p-4 space-y-3">
                        <h4 className="font-medium text-sm">
                          How to Find the Root Cause:
                        </h4>
                        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                          {analysisResult.testing_guidance.how_to_find_cause.map(
                            (step, index) => (
                              <li key={index}>{step}</li>
                            ),
                          )}
                        </ol>
                      </div>
                      <div className="rounded-lg border p-4 space-y-3">
                        <h4 className="font-medium text-sm">
                          Recommended Checks:
                        </h4>
                        <div className="space-y-3">
                          {analysisResult.testing_guidance.recommended_checks.map(
                            (check, index) => (
                              <div key={index} className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary">
                                    {check.check_type}
                                  </Badge>
                                </div>
                                <p className="text-sm">{check.description}</p>
                                {check.specific_prompts_to_test &&
                                  check.specific_prompts_to_test.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium mb-1">
                                        Prompts to test:
                                      </p>
                                      <div className="flex flex-wrap gap-1">
                                        {check.specific_prompts_to_test.map(
                                          (prompt, pIndex) => (
                                            <Badge
                                              key={pIndex}
                                              variant="outline"
                                              className="text-xs"
                                            >
                                              {prompt}
                                            </Badge>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                )}

                {!analyzing && !analysisResult && (
                  <div className="text-center text-muted-foreground py-8">
                    Click "Analyze with AI" to get insights about this project's
                    coding logs.
                  </div>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
